import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  GtdGlobeService,
  GtdStats,
  GtdAttackTypeCount,
  GtdStateHotspot,
  GtdGroupCount
} from '../../../../core/services/gtd-globe.service';
import { Observable, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-gtd-control-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './gtd-control-panel.component.html',
  styleUrl: './gtd-control-panel.component.css',
})
export class GtdControlPanelComponent implements OnInit, OnDestroy {
  stats$: Observable<GtdStats>;
  attackTypes$: Observable<GtdAttackTypeCount[]>;
  stateHotspots$: Observable<GtdStateHotspot[]>;
  topGroups$: Observable<GtdGroupCount[]>;
  isLayerVisible$: Observable<boolean>;
  is3dCylinders$: Observable<boolean>;
  isPanelOpen$: Observable<boolean>;
  isLoading$: Observable<boolean>;

  activeTab: 'filters' | 'hotspots' | 'timeline' = 'filters';
  selectedDecade: string = 'ALL';
  private destroy$ = new Subject<void>();

  constructor(public gtdService: GtdGlobeService) {
    this.stats$ = this.gtdService.stats$;
    this.attackTypes$ = this.gtdService.attackTypes$;
    this.stateHotspots$ = this.gtdService.stateHotspots$;
    this.topGroups$ = this.gtdService.topGroups$;
    this.isLayerVisible$ = this.gtdService.isLayerVisible$;
    this.is3dCylinders$ = this.gtdService.is3dCylinders$;
    this.isPanelOpen$ = this.gtdService.isPanelOpen$;
    this.isLoading$ = this.gtdService.isLoading$;
  }

  ngOnInit(): void {
    this.gtdService.filterReset$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.selectedDecade = 'ALL';
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleLayer(event: Event): void {
    event.stopPropagation();
    this.gtdService.toggleLayer();
  }

  toggle3dCylinders(event: Event): void {
    event.stopPropagation();
    this.gtdService.toggle3dCylinders();
  }

  togglePanel(): void {
    this.gtdService.togglePanel();
  }

  onSearchChange(value: string): void {
    this.gtdService.setSearchQuery(value);
  }

  clearSearch(): void {
    this.gtdService.filterState.searchQuery = '';
    this.gtdService.applyFiltersAndRender();
  }

  onDecadeSelect(decade: string): void {
    this.selectedDecade = decade;
    if (decade === 'ALL') {
      this.gtdService.setYearRange(1976, 2021);
    } else if (decade === '2015+') {
      this.gtdService.setYearRange(2015, 2021);
    } else if (decade === '2010-2014') {
      this.gtdService.setYearRange(2010, 2014);
    } else if (decade === '2000-2009') {
      this.gtdService.setYearRange(2000, 2009);
    } else if (decade === 'PRE-2000') {
      this.gtdService.setYearRange(1976, 1999);
    }
  }

  onYearChange(): void {
    this.gtdService.setYearRange(
      this.gtdService.filterState.minYear,
      this.gtdService.filterState.maxYear
    );
  }

  toggleAttackType(typeName: string): void {
    this.gtdService.toggleAttackTypeFilter(typeName);
  }

  isAttackTypeSelected(typeName: string): boolean {
    if (this.gtdService.filterState.selectedAttackTypes.size === 0) {
      return true; // none selected means all active
    }
    return this.gtdService.filterState.selectedAttackTypes.has(typeName);
  }

  onGroupChange(groupName: string): void {
    this.gtdService.selectGroupFilter(groupName);
  }

  onStateClick(state: string, event: Event): void {
    event.stopPropagation();
    this.gtdService.flyToState(state);
  }

  onResetOverview(event: Event): void {
    event.stopPropagation();
    this.gtdService.flyToNigeria();
  }

  onResetFilters(event: Event): void {
    event.stopPropagation();
    this.selectedDecade = 'ALL';
    this.gtdService.resetAllFilters();
  }
}
